import React from "react";
import { Link } from "react-router-dom";
import siteConfig, { formatPrice } from "../Datas/siteConfig";
import PolicyDocument, {
  POLICY_UPDATED_ISO,
  POLICY_UPDATED_LABEL,
} from "./PolicyDocument";
import "../css/delivery.scss";

const supportEmail = siteConfig.contact.customerServiceEmail;

export const policyDirectory = [
  {
    id: "shopping",
    title: "Shopping & orders",
    description:
      "The practical rules that apply from checkout through delivery, care and after-sales support.",
    links: [
      ["Orders & cancellations", "/orders"],
      ["Payment & billing", "/payments"],
      ["Shipping", "/shipping"],
      ["Customs, duties & taxes", "/customs"],
      ["Returns", "/returns"],
      ["Refunds", "/refunds"],
      ["Warranty & faulty goods", "/warranty"],
      ["Product care & safety", "/product-care"],
    ],
  },
  {
    id: "offers",
    title: "Offers & value",
    description:
      "Conditions for optional promotions, discount codes, gift cards and store credit.",
    links: [
      ["Promotions & discounts", "/promotions"],
      ["Gift cards & store credit", "/gift-cards"],
    ],
  },
  {
    id: "privacy",
    title: "Privacy & access",
    description:
      "How the store handles information, browser storage, privacy requests and accessible service.",
    links: [
      ["Privacy policy", "/privacy"],
      ["Privacy choices", "/privacy-choices"],
      ["Cookie policy", "/cookies"],
      ["Accessibility statement", "/accessibility"],
    ],
  },
  {
    id: "legal",
    title: "Legal & responsible use",
    description:
      "The rules for using this store, its original content and its customer-resolution process.",
    links: [
      ["Terms & conditions", "/terms"],
      ["Acceptable use", "/acceptable-use"],
      ["Intellectual property", "/intellectual-property"],
      ["Website disclaimer", "/disclaimer"],
      ["Complaints & resolution", "/complaints"],
      ["Legal notice", "/legal-notice"],
    ],
  },
];

export function PolicyCenter() {
  return (
    <main className="policy-page" id="main-content">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Customer care</p>
        <h1>Policy centre</h1>
        <p>
          Every store policy in one place, written for {siteConfig.brandName}
          customers and organised by topic.
        </p>
        <p className="policy-page__updated">
          Last reviewed{" "}
          <time dateTime={POLICY_UPDATED_ISO}>{POLICY_UPDATED_LABEL}</time>
        </p>
      </header>

      <dl className="policy-page__summary" aria-label="Policy centre at a glance">
        <div>
          <dt>Policy documents</dt>
          <dd>20 clear guides</dd>
        </div>
        <div>
          <dt>Customer care</dt>
          <dd>{supportEmail}</dd>
        </div>
        <div>
          <dt>Your rights</dt>
          <dd>Mandatory rights stay protected</dd>
        </div>
      </dl>

      <div className="policy-page__layout">
        <aside className="policy-page__aside" aria-label="Policy categories">
          <p>Browse by category</p>
          <ol>
            {policyDirectory.map((group) => (
              <li key={group.id}>
                <a href={`#${group.id}`}>{group.title}</a>
              </li>
            ))}
          </ol>
          <div className="policy-page__help">
            <span>Can&rsquo;t find an answer?</span>
            <Link to="/contact">Contact customer care</Link>
          </div>
        </aside>

        <article className="policy-page__content">
          {policyDirectory.map((group, index) => (
            <section id={group.id} key={group.id}>
              <span className="policy-page__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2>{group.title}</h2>
                <p>{group.description}</p>
                <ul>
                  {group.links.map(([label, route]) => (
                    <li key={route}>
                      <Link to={route}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </article>
      </div>
    </main>
  );
}

const orderSections = [
  {
    id: "placing",
    title: "Placing an order",
    body: [
      "Review the product, variant, quantity, delivery address and total before submitting checkout. You must provide current, complete information and be authorised to use the selected payment method.",
      "Items in a shopping bag are not reserved. Stock, price and delivery availability are confirmed during checkout and may change until the order is accepted.",
    ],
  },
  {
    id: "acceptance",
    title: "Acknowledgement & acceptance",
    body: [
      "An on-screen message or automatic email means we received the order request; it is not an acceptance. We accept an order when we send a dispatch or express acceptance notice.",
      "If we cannot accept all or part of an order, we will explain where reasonably possible and release or refund any amount already collected for the cancelled item.",
    ],
  },
  {
    id: "changes",
    title: "Changes after checkout",
    body: [
      "Contact customer care immediately if the address, colour, size or quantity is wrong. We will try to help before packing begins, but a submitted order cannot always be changed and carriers may not permit redirection.",
      "For security, we may ask you to confirm identity or address details. We will never ask for a full card number by email.",
    ],
    contact: true,
    contactSuffix: " with the order number and requested change.",
  },
  {
    id: "cancellation",
    title: "Customer cancellations",
    body: [
      "You may request cancellation before dispatch. If packing or carrier collection has already started, the order may need to arrive and follow the returns process instead.",
      "A cancellation right provided by applicable consumer law remains available on its legal terms. Exceptions may apply to personalised, altered, hygiene-sealed or other excluded goods where the law permits.",
    ],
    route: "/returns",
    routeLabel: "Review the returns policy",
  },
  {
    id: "preorders",
    title: "Pre-orders, backorders & limited pieces",
    body: [
      "If an item is sold before it is ready to ship, the product page will identify it as a pre-order or made-to-order item and provide an estimated dispatch period. Products purchased together may ship separately.",
      "Production dates are estimates. If a material delay occurs, we will provide an update and any cancellation option required by applicable law.",
    ],
  },
  {
    id: "limits",
    title: "Order limits & verification",
    body: [
      "We may apply reasonable purchase limits, refuse automated or unusually high-volume orders, or pause an order for payment, address or fraud checks. These controls protect customers and limited stock.",
      "We may cancel before acceptance where stock is unavailable, an obvious error occurred, delivery is not supported, payment fails or the order appears unlawful or abusive. Decisions will not be based on a protected characteristic.",
    ],
  },
];

export function OrdersPolicy() {
  return (
    <PolicyDocument
      title="Orders & cancellations"
      intro="What happens after you press order, including acceptance, amendments, cancellations and limited-stock checks."
      highlights={[
        { label: "Order status", value: "Accepted on confirmation or dispatch" },
        { label: "Need a change?", value: "Contact us before packing" },
        { label: "Consumer rights", value: "Never reduced by this policy" },
      ]}
      sections={orderSections}
      helpLabel="Order question?"
    />
  );
}

const paymentSections = [
  {
    id: "methods",
    title: "Available payment methods",
    body: [
      "Checkout shows the payment methods currently available for your device, currency and destination. A method displayed elsewhere on the site may not be available for every order.",
      "The person placing the order must be authorised to use the chosen card, wallet or account. We do not accept payment details sent by ordinary email.",
    ],
  },
  {
    id: "authorisation",
    title: "Authorisation & capture",
    body: [
      "A payment provider may place a temporary authorisation hold when checkout is submitted. Depending on the method, funds may be captured at order submission, acceptance or dispatch.",
      "A declined authorisation does not create an accepted order. Your bank controls how quickly an unused hold disappears, and the timing may differ from a refund.",
    ],
  },
  {
    id: "currency",
    title: "Currency, conversion & taxes",
    body: [
      `Store prices are presented in ${siteConfig.currency} unless another currency is clearly selected. Your provider may apply its own exchange rate, international transaction fee or wallet charge.`,
      "Taxes, delivery and any prepaid destination charges are itemised before the order is submitted when they can be calculated. Charges collected by customs or a local carrier are covered separately in our customs policy.",
    ],
    route: "/customs",
    routeLabel: "Read customs, duties & taxes",
  },
  {
    id: "security",
    title: "Payment security",
    body: [
      "Full card credentials are entered into the checkout or payment provider environment and are not intended to be collected through customer-care messages. Providers may use authentication, tokenisation and fraud screening.",
      "We may ask for limited information needed to confirm an order, but we will not request a password, one-time passcode, card PIN or complete card number by email.",
    ],
  },
  {
    id: "problems",
    title: "Failed, pending or duplicate payments",
    body: [
      "If checkout fails, first check whether an order confirmation was issued before trying again. A pending entry may be an authorisation rather than a completed charge.",
      "Send us the order number, payment date, amount and the last four digits or provider reference if a duplicate completed charge appears. Do not send full financial credentials.",
    ],
    contact: true,
    contactSuffix: " for payment support.",
  },
  {
    id: "chargebacks",
    title: "Disputes & chargebacks",
    body: [
      "Contact us first if you do not recognise a charge or believe an order was not fulfilled. We can check order and delivery records and may resolve a genuine error more quickly.",
      "Nothing in this policy prevents you from using rights available through your payment provider. Knowingly filing a false dispute or chargeback may be treated as misuse and referred with relevant records to the provider.",
    ],
  },
];

export function PaymentsPolicy() {
  return (
    <PolicyDocument
      title="Payment & billing"
      intro="How checkout charges, authorisations, currency conversion and payment questions are handled."
      highlights={[
        { label: "Store currency", value: siteConfig.currency },
        { label: "Card data", value: "Handled by the checkout provider" },
        { label: "Billing support", value: supportEmail },
      ]}
      sections={paymentSections}
      helpLabel="Billing question?"
    />
  );
}

const refundSections = [
  {
    id: "eligibility",
    title: "When a refund is available",
    body: [
      "Approved returns, accepted cancellations, unavailable items and verified faulty or incorrect goods may qualify for a full or partial refund. The reason for the refund determines which delivery or return costs are included.",
      "A refund does not replace rights or remedies that applicable consumer law gives you. The returns and warranty policies explain the evidence and condition requirements for different situations.",
    ],
    route: "/returns",
    routeLabel: "Check return eligibility",
  },
  {
    id: "method",
    title: "Refund method",
    body: [
      "Refunds normally go back to the original payment method and currency. We cannot redirect a card refund to another person, card or bank account.",
      "Where the original method is unavailable, we will use a lawful alternative agreed with you. Gift-card or store-credit purchases are normally returned to the same form of value unless the law requires otherwise.",
    ],
  },
  {
    id: "amount",
    title: "Refund amount",
    body: [
      "The amount depends on the item price paid after discounts and on the reason for return. Optional express-delivery upgrades, return postage and destination charges are not normally refunded for a change-of-mind return.",
      "We may make a lawful reduction for value lost through handling beyond what is reasonably needed to inspect a product. We will explain any reduction before completing the refund.",
    ],
  },
  {
    id: "timing",
    title: "Processing time",
    body: [
      "After an approved return reaches us, allow up to 10 business days for inspection and refund submission. An accepted pre-dispatch cancellation is normally submitted sooner.",
      "Banks, wallets and card networks control when the credit appears after submission. Currency movements or provider fees can mean the displayed converted amount differs from the original converted charge.",
    ],
  },
  {
    id: "partial",
    title: "Partial orders & bundled offers",
    body: [
      "When only part of an order is refunded, any discount that depended on quantity, bundle value or a spending threshold may be recalculated as described in the promotion terms.",
      "If we cancel an item from a multi-item order, we refund that item and any delivery charge that no longer applies. We will not charge more than the accepted order total without your agreement.",
    ],
  },
  {
    id: "missing",
    title: "Late or missing refunds",
    body: [
      "Check the original payment account and allow the provider's posting period. If the credit is still missing, ask the provider whether it can see a pending refund.",
      "We can supply the refund date, amount and available transaction reference. Send only the minimum information needed to locate the order.",
    ],
    contact: true,
    contactSuffix: " with your order number if the provider cannot locate it.",
  },
];

export function RefundPolicy() {
  return (
    <PolicyDocument
      title="Refunds"
      intro="How approved refunds are calculated, returned and tracked after a cancellation, return or order issue."
      highlights={[
        { label: "Refund route", value: "Original payment method" },
        { label: "Our processing", value: "Up to 10 business days" },
        { label: "Your protection", value: "Statutory remedies remain" },
      ]}
      sections={refundSections}
      helpLabel="Refund question?"
    />
  );
}

const warrantySections = [
  {
    id: "rights",
    title: "Legal guarantees come first",
    body: [
      "Products must meet any quality, description, durability and fitness standards required by the consumer law that applies to the purchase. This policy adds a practical reporting process and does not replace or shorten a mandatory legal guarantee.",
      "Remedies and time limits can differ by location. We will not ask you to waive a non-excludable right as a condition of support.",
    ],
  },
  {
    id: "fault",
    title: "What may be a fault",
    body: [
      "A manufacturing issue, material failure under normal use, unsafe condition or material difference from the product description may be a fault. Normal wear, accidental damage and expected variation are assessed separately.",
      "Hand-finished colour, print placement, weave and small dimensional differences can be characteristic of a piece. A variation is not automatically a defect, but it must remain consistent with the description and any sample shown.",
    ],
  },
  {
    id: "reporting",
    title: "How to report a problem",
    body: [
      "Stop using an item if continued use could cause damage or create a safety concern. Contact us with the order number, a concise description, when the issue appeared and clear photographs of the item, label and packaging where relevant.",
      "Do not send the product until return instructions are provided. We may request reasonable additional information needed to assess the issue, but we will not require evidence that consumer law says is unnecessary.",
    ],
    contact: true,
    contactSuffix: " to open a product assessment.",
  },
  {
    id: "assessment",
    title: "Assessment",
    body: [
      "We may examine the product, its care history and the circumstances of use. A reasonable inspection does not reduce your legal rights, and an initial photograph review is not a final rejection.",
      "If no covered fault is found, we will explain the basis of the decision and discuss return delivery. You may still use any independent or regulatory process available to you.",
    ],
  },
  {
    id: "remedies",
    title: "Repair, replacement or refund",
    body: [
      "The available remedy depends on applicable law, the nature of the problem, product age, stock and whether repair is reasonable. A remedy may include repair, replacement, price reduction, refund or another solution agreed with you.",
      "For a confirmed covered fault, we pay reasonable authorised return costs. Replacement items are subject to stock and do not erase any rights that continue by law.",
    ],
  },
  {
    id: "exclusions",
    title: "What is not a manufacturing fault",
    body: [
      "Coverage does not normally extend to misuse, unauthorised alteration or repair, failure to follow care instructions, ordinary ageing, abrasion, accidental damage, improper storage, or use outside the product's described purpose.",
      "We assess each request fairly. An exclusion is used only to the extent permitted by law and only where it is relevant to the reported issue.",
    ],
  },
];

export function WarrantyPolicy() {
  return (
    <PolicyDocument
      title="Warranty & faulty goods"
      intro="A fair process for product faults, safety concerns and remedies, alongside your non-excludable consumer rights."
      highlights={[
        { label: "First step", value: "Stop use if safety is affected" },
        { label: "Assessment", value: "Order details & clear photos" },
        { label: "Possible remedies", value: "Repair, replace or refund" },
      ]}
      sections={warrantySections}
      helpLabel="Product problem?"
    />
  );
}

const promotionSections = [
  {
    id: "scope",
    title: "Offer-specific terms",
    body: [
      "A promotion may have its own dates, eligible products, customer group, territory, minimum spend, maximum benefit or redemption channel. Those offer-specific details form part of this policy.",
      "Unless the offer says otherwise, promotions are available only while advertised stock and the stated redemption period last.",
    ],
  },
  {
    id: "codes",
    title: "Using a discount code",
    body: [
      "Enter the code in the designated checkout field before submitting payment. Codes cannot normally be applied after an order is accepted, exchanged for cash or combined with another offer.",
      "Only one use is permitted per customer, household, account, email address or payment method when the offer is described as single-use.",
    ],
  },
  {
    id: "exclusions",
    title: "Common exclusions",
    body: [
      "Unless expressly included, an offer may exclude gift cards, delivery, taxes, duties, already reduced items, collaborations, pre-orders, made-to-order pieces and specified limited editions.",
      "A minimum-spend threshold is calculated after excluded items and other discounts and before delivery or destination charges, unless the offer states a different calculation.",
    ],
  },
  {
    id: "returns",
    title: "Returns from a promotion",
    body: [
      "A refund is based on the amount actually paid for the returned item. If a partial return means the retained order no longer meets a bundle or threshold condition, the benefit may be recalculated where the original offer clearly allowed it.",
      "Free gifts must be returned unused when the qualifying purchase is returned in full, unless the offer or applicable law says otherwise.",
    ],
    route: "/refunds",
    routeLabel: "Read the refund policy",
  },
  {
    id: "fairness",
    title: "Fair use & availability",
    body: [
      "Codes may not be sold, reproduced through automated methods or used to place deceptive, wholesale or resale orders contrary to the offer. We may withhold an unearned benefit after a reasonable review of suspected misuse.",
      "We may end or correct an offer for future orders if a technical error, unauthorised publication or event outside reasonable control makes it unworkable. Accepted orders and mandatory rights remain protected.",
    ],
  },
  {
    id: "pricing",
    title: "Price changes & adjustments",
    body: [
      "Prices and future promotions may change. A later sale does not automatically alter an accepted order, and we do not promise a retrospective price adjustment unless the relevant offer expressly provides one.",
      "If an advertised price is an obvious error, the order terms explain the options available before acceptance.",
    ],
  },
];

export function PromotionsPolicy() {
  return (
    <PolicyDocument
      title="Promotions & discounts"
      intro="General conditions for discount codes, bundles, gifts and limited-time store offers."
      highlights={[
        { label: "Apply", value: "Before submitting checkout" },
        { label: "Combination", value: "One offer unless stated" },
        { label: "Refund value", value: "Based on the price paid" },
      ]}
      sections={promotionSections}
      helpLabel="Code not working?"
    />
  );
}

const giftCardSections = [
  {
    id: "availability",
    title: "When gift cards are offered",
    body: [
      "This policy applies only when a gift card or store credit is expressly offered by the store. The product or issue notice will state its value, currency, delivery method and any additional conditions before purchase or acceptance.",
      "A gift card is a payment method for eligible store products; it is not a bank account, credit card or investment product.",
    ],
  },
  {
    id: "delivery",
    title: "Purchase & delivery",
    body: [
      "The buyer is responsible for entering the intended recipient's email address correctly. Digital delivery may be delayed by security checks, scheduled delivery settings or email filtering.",
      "Tell us promptly if a newly purchased code does not arrive. We may verify the purchaser and order before reissuing access information.",
    ],
  },
  {
    id: "redemption",
    title: "Redemption & balance",
    body: [
      "Enter a valid code in the gift-card or store-credit field at checkout. The available balance is applied to eligible charges, and another offered payment method may be used for any remainder.",
      "Unless stated otherwise, balances are denominated in the currency in which they were issued. Conversion, cross-store use and cash withdrawal are not supported unless required by law.",
    ],
  },
  {
    id: "expiry",
    title: "Expiry & inactivity",
    body: [
      "Any expiry date or inactivity condition will be disclosed when value is issued and will apply only where lawful. Where the law prohibits or extends an expiry, the mandatory rule controls.",
      "We do not deduct an inactivity fee unless it is clearly disclosed and legally permitted.",
    ],
  },
  {
    id: "refunds",
    title: "Returns & refunds",
    body: [
      "An item bought with store value may be refunded back to the same gift card or credit. If more than one method was used, the refund may be divided between them according to the original payment.",
      "Gift-card purchases are not redeemable for cash except where required by law. Statutory cancellation or refund rights, where applicable, are unaffected.",
    ],
  },
  {
    id: "loss",
    title: "Lost codes, fraud & resale",
    body: [
      "Treat a gift-card code like cash. We may replace a lost code only after verifying the original purchase and remaining balance, and cannot restore value already used by an unauthorised person before notice where the law permits.",
      "Codes may not be counterfeited, resold through unauthorised channels or used for unlawful payment activity. We may pause suspicious value while we investigate without affecting a genuine holder's legal rights.",
    ],
    contact: true,
    contactSuffix: " for gift-card support.",
  },
];

export function GiftCardsPolicy() {
  return (
    <PolicyDocument
      title="Gift cards & store credit"
      intro="The rules that apply if gift cards or store credit are made available through the Reamflora store."
      highlights={[
        { label: "Availability", value: "Only when expressly offered" },
        { label: "Refund route", value: "Usually back to store value" },
        { label: "Legal rules", value: "Local mandatory terms apply" },
      ]}
      sections={giftCardSections}
      helpLabel="Gift-card question?"
    />
  );
}

const productCareSections = [
  {
    id: "instructions",
    title: "Follow the item instructions",
    body: [
      "Read the product page, sewn-in label, packaging and any supplied care card before cleaning or use. Item-specific directions take priority over general guidance on this page.",
      "Test an unfamiliar cleaning method on a discreet area first. When professional cleaning is specified, tell the cleaner about trims, prints, coatings and mixed materials.",
    ],
  },
  {
    id: "handling",
    title: "Gentle handling & storage",
    body: [
      "Keep pieces dry, clean and away from direct heat, prolonged sunlight and rough surfaces. Store shaped accessories supported rather than crushed, and avoid hanging garments in a way that stretches them.",
      "Remove surface dust with a suitable soft tool. Do not use bleach, strong solvent, high heat or vigorous machine cycles unless the item instructions expressly permit them.",
    ],
  },
  {
    id: "colour",
    title: "Colour, print & natural variation",
    body: [
      "Deep colours and printed surfaces can transfer when new, wet or rubbed against pale materials. Keep them separate during early wear and cleaning and follow colour-care instructions.",
      "Hand-led processes and small production runs can create individual print placement, shade and texture. These details should not affect safe use or the product's compliance with its description.",
    ],
  },
  {
    id: "safety",
    title: "Safe use",
    body: [
      "Use a product only for its described purpose and keep packaging, cords, pins and detachable small parts away from children and animals. Clothing and accessories are not protective equipment unless specifically certified and described that way.",
      "Stop using a piece if a component becomes loose, sharp, unusually hot, irritating or otherwise unsafe. Keep the item and contact us so the concern can be assessed.",
    ],
    route: "/warranty",
    routeLabel: "Report a safety or quality issue",
  },
  {
    id: "materials",
    title: "Materials & sensitivities",
    body: [
      "Material information available to us is shown on the product page or label. If you have a known sensitivity, check the listed composition before purchase and ask customer care when more detail is needed.",
      "Discontinue contact and seek appropriate professional advice if a reaction occurs. Customer care cannot provide medical diagnosis or treatment guidance.",
    ],
  },
  {
    id: "repair",
    title: "Repair, reuse & disposal",
    body: [
      "Minor maintenance can extend a product's life, but an unsuitable repair may affect appearance or safety. Ask us before altering an item that may have a manufacturing issue.",
      "Reuse, donate or recycle items and packaging where suitable local facilities exist. Remove personal information from labels or paperwork before disposal.",
    ],
  },
];

export function ProductCarePolicy() {
  return (
    <PolicyDocument
      title="Product care & safety"
      intro="General guidance for enjoying, cleaning and storing pieces safely while preserving their colour and shape."
      highlights={[
        { label: "Best source", value: "The item's own care label" },
        { label: "Cleaning", value: "Gentle, tested & material-aware" },
        { label: "Safety concern", value: "Stop use and tell us" },
      ]}
      sections={productCareSections}
      helpLabel="Need care advice?"
    />
  );
}

const customsSections = [
  {
    id: "scope",
    title: "Cross-border orders",
    body: [
      "An order that crosses a customs border may be reviewed by authorities and assessed import duty, tax, brokerage, storage or handling charges. These charges are separate from ordinary store delivery unless checkout expressly says they are prepaid.",
      "Customs rules and thresholds vary and can change. Information on this page is general shopping guidance, not tax or customs advice.",
    ],
  },
  {
    id: "importer",
    title: "Importer responsibilities",
    body: [
      "Unless checkout identifies another arrangement, the recipient is the importer of record and is responsible for complying with destination rules and paying charges collected on import.",
      "A carrier or authority may request an identification number, proof of purchase or other lawful clearance information directly from the recipient.",
    ],
  },
  {
    id: "declarations",
    title: "Accurate declarations",
    body: [
      "We describe the goods and declare the commercial value in shipping documents as required. We cannot mark a purchase as a gift, reduce its value, use a false classification or provide inaccurate origin information to avoid charges.",
      "An order may ship in more than one parcel, and each parcel may be reviewed separately under destination procedures.",
    ],
  },
  {
    id: "estimates",
    title: "Estimates & collection",
    body: [
      "Any checkout estimate is based on the information available at the time. The destination authority determines final classification, valuation and collection unless duties are confirmed as included.",
      "Contact the local customs authority or carrier for a binding explanation of a destination charge. We can provide an invoice or available shipment documentation for an order.",
    ],
  },
  {
    id: "delays",
    title: "Clearance delays & restrictions",
    body: [
      "Border review may extend delivery beyond the carrier estimate. We cannot control inspections, local service interruptions or a recipient's delay in providing requested clearance information.",
      "We may restrict checkout to a destination when a product, material, value or carrier service cannot lawfully or reliably be delivered there.",
    ],
  },
  {
    id: "refusal",
    title: "Refused or unpaid import charges",
    body: [
      "If a parcel is refused, abandoned or returned because destination charges were not paid, any refund is assessed after the item returns in acceptable condition. Lawful outbound, return, storage or customs costs may be deducted where permitted.",
      "We do not deduct costs caused by our error or override any cancellation or refund right that applies by law.",
    ],
    route: "/refunds",
    routeLabel: "Read the refund policy",
  },
];

export function CustomsPolicy() {
  return (
    <PolicyDocument
      title="Customs, duties & taxes"
      intro="What to expect when an order crosses a border and local authorities collect destination charges."
      highlights={[
        { label: "Checkout", value: "Shows prepaid charges when available" },
        { label: "Declarations", value: "Always accurate & lawful" },
        { label: "Final assessment", value: "Made by destination authorities" },
      ]}
      sections={customsSections}
      helpLabel="Need order documents?"
    />
  );
}

const accessibilitySections = [
  {
    id: "commitment",
    title: "Our commitment",
    body: [
      "We want people with different abilities, devices and ways of interacting to be able to browse products, understand policies and complete a purchase with dignity and independence.",
      "Accessibility is ongoing work. We review changes and prioritise issues that block shopping, account access or customer support.",
    ],
  },
  {
    id: "features",
    title: "Accessibility features",
    body: [
      "The store is designed to support keyboard navigation, visible focus, semantic headings, labelled controls, meaningful link text, alternative text and responsive enlargement.",
      "Motion is reduced when a device requests it. Colour and layout are selected for legibility, while product information is provided in text rather than only through images where practical.",
    ],
  },
  {
    id: "compatibility",
    title: "Browsers & assistive technology",
    body: [
      "For the best experience, use a current browser and operating system with security and accessibility updates installed. Older combinations may not interpret newer web standards consistently.",
      "If a control does not work with a keyboard, screen reader, voice input, magnifier or another tool, tell us the page, device, browser and tool involved if you are comfortable doing so.",
    ],
  },
  {
    id: "third-party",
    title: "Third-party services",
    body: [
      "Checkout, payment, maps, social content or delivery tracking may be provided by third parties. We do not control every part of those services, but we welcome reports and will offer a reasonable alternative where we can.",
      "A third-party limitation does not remove our responsibility to provide customer support or any adjustment required by applicable law.",
    ],
  },
  {
    id: "alternative",
    title: "Alternative assistance",
    body: [
      "Customer care can provide product, policy or order information in a reasonable alternative format and can help with a transaction that the website prevents you from completing.",
      "We will never charge more because a customer needs accessibility assistance. Product availability and the same security checks still apply.",
    ],
    contact: true,
    contactSuffix: " and describe the assistance or format you need.",
  },
  {
    id: "feedback",
    title: "Feedback & review",
    body: [
      "Accessibility feedback is logged for review. Please avoid sending medical information; we need only enough detail to understand the barrier and reply.",
      "This statement is reviewed as the store changes. Contacting us does not limit any formal accessibility complaint right available in your location.",
    ],
  },
];

export function AccessibilityPolicy() {
  return (
    <PolicyDocument
      eyebrow="Accessibility"
      title="Accessibility statement"
      intro="Our approach to accessible browsing, checkout, customer support and continuous improvement."
      highlights={[
        { label: "Design goal", value: "Independent, inclusive shopping" },
        { label: "Alternative help", value: "Available through customer care" },
        { label: "Feedback", value: supportEmail },
      ]}
      sections={accessibilitySections}
      helpLabel="Found a barrier?"
      helpText="Send accessibility feedback"
    />
  );
}

const intellectualPropertySections = [
  {
    id: "ownership",
    title: "Store content",
    body: [
      `Unless a credit states otherwise, the original text, artwork, photographs, product styling, graphics, logos, layout and other creative material on this store are owned by ${siteConfig.brandName} or used under permission. Applicable copyright, trademark, design and other laws protect that material.`,
      "A purchase transfers ownership of the physical product only; it does not transfer rights in its artwork, print, pattern, product photography, name or design.",
    ],
  },
  {
    id: "permitted",
    title: "Permitted personal use",
    body: [
      "You may view the site, keep an ordinary order record, print a policy for personal reference and share a standard link to a public page.",
      "A limited personal use does not permit removal of credits, creation of derivative merchandise, commercial republication or any use that suggests endorsement or partnership.",
    ],
  },
  {
    id: "restricted",
    title: "Uses that need permission",
    body: [
      "Written permission is required before reproducing product imagery or text for commercial use, copying a print or product design, training a commercial model on protected store content, minting a digital asset, or using the brand in advertising, domains or marketplace listings.",
      "Search engines may index public pages in the ordinary way. Automated collection beyond that ordinary function remains subject to our acceptable-use policy and applicable law.",
    ],
    route: "/acceptable-use",
    routeLabel: "Read acceptable use",
  },
  {
    id: "marks",
    title: "Names, marks & look",
    body: [
      `${siteConfig.wordmark}, the Reamflora word name, logos and distinctive source identifiers may be trademarks or protected trade dress. Similarity alone does not establish permission to use them.`,
      "References made only to identify an authentic product must be accurate, limited and must not imply affiliation. Counterfeit or misleading listings may be reported to the relevant platform or authority.",
    ],
  },
  {
    id: "submissions",
    title: "Customer materials",
    body: [
      "You keep ownership of original material you send us. When you submit content for a review, feature or other public use, any licence we request will be described at the point of submission; private support messages are not made public merely because they are sent.",
      "Do not submit material you do not own or have permission to use, or material that violates privacy, publicity or intellectual-property rights.",
    ],
  },
  {
    id: "notices",
    title: "Intellectual-property concerns",
    body: [
      "A rights holder may send a notice identifying the protected work, the specific store material at issue, the basis of the claim, contact details and a good-faith statement that the information is accurate.",
      "We review sufficiently detailed notices and may remove or restrict material while a concern is assessed. A report must not knowingly misrepresent ownership or infringement.",
    ],
    contact: true,
    contactSuffix: " with the subject “IP notice”.",
  },
];

export function IntellectualPropertyPolicy() {
  return (
    <PolicyDocument
      title="Intellectual property"
      intro="Respectful rules for Reamflora's original store content, product designs, marks and customer-submitted material."
      highlights={[
        { label: "Personal use", value: "Viewing, records & ordinary links" },
        { label: "Commercial reuse", value: "Written permission required" },
        { label: "Rights notices", value: supportEmail },
      ]}
      sections={intellectualPropertySections}
      helpLabel="Rights concern?"
    />
  );
}

const acceptableUseSections = [
  {
    id: "purpose",
    title: "Permitted purpose",
    body: [
      "Use the store to learn about products, shop for lawful personal or authorised business purposes, manage genuine orders and communicate with customer care.",
      "You must follow applicable law and these store policies and must be old enough to enter a purchase contract in your location or act with valid guardian permission.",
    ],
  },
  {
    id: "security",
    title: "Security & technical integrity",
    body: [
      "Do not bypass access controls, probe for vulnerabilities without written authorisation, introduce malware, interfere with another visitor, overload the service or use credentials that are not yours.",
      "Do not use bots to defeat product limits, submit fraudulent orders, harvest personal information or copy protected content at unreasonable scale. Responsible security concerns may be reported privately to customer care.",
    ],
  },
  {
    id: "accounts",
    title: "Accounts & identity",
    body: [
      "Keep account and checkout information accurate and protect passwords, codes and devices. Do not impersonate another person, conceal an unauthorised payment user or create accounts to evade restrictions.",
      "Tell us promptly about suspected unauthorised access. We may require proportionate verification before changing account or order details.",
    ],
  },
  {
    id: "commerce",
    title: "Fair shopping conduct",
    body: [
      "Do not manipulate promotions, file knowingly false payment disputes, place orders for unlawful export, or use multiple identities to circumvent clearly disclosed purchase limits.",
      "Personal resale of an authentic item does not create a partnership with us. You must not describe yourself as an authorised retailer or use store assets in a misleading listing.",
    ],
  },
  {
    id: "communications",
    title: "Messages & submissions",
    body: [
      "Communications must not threaten, harass, discriminate, deceive, disclose another person's private information or include unlawful or infringing material.",
      "Product feedback may be critical, but it must reflect a genuine experience and must not be exchanged for an undisclosed benefit or generated to mislead customers.",
    ],
  },
  {
    id: "response",
    title: "How we respond to misuse",
    body: [
      "We may limit a feature, cancel an unaccepted order, preserve evidence or suspend access when reasonably necessary to protect the store, customers or third parties. We consider severity, context and the availability of a less restrictive response.",
      "Serious unlawful conduct may be reported to providers or authorities as permitted by law. A restriction does not remove payment, refund or consumer rights that have already arisen.",
    ],
  },
];

export function AcceptableUsePolicy() {
  return (
    <PolicyDocument
      title="Acceptable use"
      intro="The ground rules that keep the Reamflora store, its customers and its original work safe."
      highlights={[
        { label: "Purpose", value: "Lawful browsing & genuine shopping" },
        { label: "Automation", value: "No abuse, evasion or harvesting" },
        { label: "Customer rights", value: "Remain protected" },
      ]}
      sections={acceptableUseSections}
      helpLabel="Report misuse?"
    />
  );
}

const disclaimerSections = [
  {
    id: "information",
    title: "Store information",
    body: [
      "We take reasonable care with product, price, availability and policy information, but an occasional typographical, technical or publication error may occur. Order acceptance, correction and cancellation rules are set out in the terms and order policy.",
      "General editorial content is provided for information and is not professional, medical, legal, tax or financial advice.",
    ],
  },
  {
    id: "appearance",
    title: "Colour, scale & appearance",
    body: [
      "Screen settings, lighting, image compression and production variation can affect how colour, texture and scale appear. Measurements are approximate unless a tolerance is specifically stated.",
      "We use descriptions and images to represent products fairly. Expected visual variation does not excuse a product that is faulty or materially different from its description.",
    ],
  },
  {
    id: "availability",
    title: "Availability & service continuity",
    body: [
      "The store may be unavailable for maintenance, security work, network interruption or an event outside reasonable control. We do not guarantee that every product or feature will remain available.",
      "We may update content, retire products and correct errors, subject to accepted orders and rights that have already arisen.",
    ],
  },
  {
    id: "links",
    title: "External links & services",
    body: [
      "A link to a carrier, payment provider, social platform or other independent site is provided for convenience or to complete a service. We do not control its content, accessibility, security or privacy practices.",
      "A link is not an endorsement unless we say so expressly. Review the third party's terms before using its service.",
    ],
  },
  {
    id: "liability",
    title: "Limits that may apply",
    body: [
      "Nothing here excludes liability for fraud, deliberate misconduct, personal injury caused by negligence, breach of mandatory product obligations or any responsibility that law does not allow us to exclude.",
      "Where exclusion is legally permitted, we are not responsible for an indirect or unforeseeable loss caused by personal use of the public website, or for a business loss arising from a consumer purchase.",
    ],
  },
  {
    id: "priority",
    title: "Consumer protections have priority",
    body: [
      "This disclaimer must be read with the terms, product description and applicable consumer law. If a statement conflicts with a non-excludable right, the right controls.",
      "Contact customer care if information on the site appears unclear, inconsistent or unsafe before relying on it.",
    ],
  },
];

export function DisclaimerPolicy() {
  return (
    <PolicyDocument
      title="Website disclaimer"
      intro="Practical limits around general store content, product presentation, availability and external services."
      highlights={[
        { label: "Product visuals", value: "Accurate, with screen variation" },
        { label: "External services", value: "Governed by their own terms" },
        { label: "Mandatory rights", value: "Never excluded" },
      ]}
      sections={disclaimerSections}
      helpLabel="Information unclear?"
    />
  );
}

const privacyChoiceSections = [
  {
    id: "overview",
    title: "Your available choices",
    body: [
      "Depending on your location and our relationship with you, you may ask for access, correction, deletion, restriction, portability or details about how personal information is used. You may also object to or opt out of certain processing.",
      "A legal exception may apply, for example when information is needed for tax records, fraud prevention, a dispute or completion of an order. We will explain a refusal or limitation where required.",
    ],
  },
  {
    id: "marketing",
    title: "Marketing choices",
    body: [
      "You can use the unsubscribe control in a promotional email or contact us to stop email marketing. A marketing opt-out does not stop necessary order, security, policy or customer-service messages.",
      "We may keep a minimal suppression record so that a withdrawn address is not accidentally added again.",
    ],
  },
  {
    id: "cookies",
    title: "Cookie choices",
    body: [
      "Browser controls can remove or block stored information. Where the store offers a consent control, you can use it to change optional choices for that browser and device.",
      "Strictly necessary storage supports security, bag contents and checkout and may not be switchable through a preference panel. Blocking it in the browser can prevent features from working.",
    ],
    route: "/cookies",
    routeLabel: "Read the cookie policy",
  },
  {
    id: "sale",
    title: "Sale, sharing & targeted advertising",
    body: [
      "We do not sell personal information for money. If our practices ever fall within a local legal definition of selling, sharing or targeted advertising, we will provide the notice and opt-out method required there before using information that way.",
      "Service providers that process information only to operate the store are not treated as independent purchasers merely because they receive limited data to perform that work.",
    ],
  },
  {
    id: "request",
    title: "How to make a request",
    body: [
      "Email from the address connected with the order or account and state the privacy right you wish to use. Include your country or region and only the order references needed to locate relevant records.",
      "For your protection, we may ask for proportionate verification. Do not send a password, full payment number or unnecessary identity document in the first message.",
    ],
    contact: true,
    contactSuffix: " with the subject “Privacy request”.",
  },
  {
    id: "agent",
    title: "Agents, appeals & complaints",
    body: [
      "An authorised agent may make a request where local law permits, but we may ask for proof of authority and may verify the request directly with you.",
      "If you disagree with a decision, reply with the reason and ask for review. You may also contact the privacy or consumer authority available in your location without losing any other remedy.",
    ],
  },
  {
    id: "non-discrimination",
    title: "No retaliation",
    body: [
      "We will not unlawfully discriminate against you for using a privacy right. Ordinary differences needed to complete a requested service, prevent fraud or reflect the value of an optional lawful programme are not retaliation.",
      "The full privacy policy explains the information involved, purposes, recipients, retention and international handling in more detail.",
    ],
    route: "/privacy",
    routeLabel: "Read the privacy policy",
  },
];

export function PrivacyChoicesPolicy() {
  return (
    <PolicyDocument
      eyebrow="Privacy"
      title="Privacy choices"
      intro="A practical route to marketing, cookie and personal-information choices that may be available where you live."
      highlights={[
        { label: "Marketing", value: "Unsubscribe at any time" },
        { label: "Requests", value: "Verified and handled fairly" },
        { label: "Contact", value: supportEmail },
      ]}
      sections={privacyChoiceSections}
      helpLabel="Privacy request?"
      helpText="Contact customer care"
    />
  );
}

const complaintSections = [
  {
    id: "contact",
    title: "Start with customer care",
    body: [
      "Tell us what happened and the outcome you are seeking. Include the order number, relevant dates and concise supporting material, but do not send passwords or full payment details.",
      "We welcome good-faith complaints about products, delivery, billing, privacy, accessibility, service or store content. Making a complaint does not reduce your consumer rights.",
    ],
    contact: true,
    contactSuffix: " with the subject “Complaint”.",
  },
  {
    id: "process",
    title: "Acknowledgement & review",
    body: [
      "We aim to acknowledge a complete complaint within two business days. A substantive review may take longer when we need carrier, payment or product information.",
      "We assess the relevant order record, policy, communication and applicable mandatory rules. We may ask a focused follow-up question or request reasonable evidence.",
    ],
  },
  {
    id: "outcome",
    title: "Our response",
    body: [
      "We will explain the outcome in plain language and, where appropriate, offer a correction, replacement, repair, refund, service adjustment or other reasonable step.",
      "If we cannot provide the requested outcome, we will explain the main reason and identify any internal review option available.",
    ],
  },
  {
    id: "review",
    title: "Ask for another review",
    body: [
      "If important information was missed or you believe the policy was applied incorrectly, reply and ask for escalation. Identify the point you want reviewed rather than resending sensitive material.",
      "Where practical, a person not responsible for the original decision will review the disputed point.",
    ],
  },
  {
    id: "external",
    title: "External options",
    body: [
      "You may contact a consumer, privacy, accessibility, payment or other competent authority where applicable. You may also use a court or alternative dispute process available under local law.",
      "We do not require you to give up a mandatory remedy or statutory time limit in order to use our internal process.",
    ],
  },
  {
    id: "records",
    title: "Complaint records & respectful contact",
    body: [
      "We retain complaint records only as needed to resolve the matter, improve service, prevent misuse and meet legal record duties, in line with the privacy policy.",
      "We ask everyone involved to communicate without threats, harassment or discrimination. We may move abusive contact to a written channel while continuing to address a legitimate unresolved issue.",
    ],
  },
];

export function ComplaintsPolicy() {
  return (
    <PolicyDocument
      title="Complaints & resolution"
      intro="A straightforward process for raising a concern, receiving a reasoned response and requesting review."
      highlights={[
        { label: "How to begin", value: "Email with “Complaint”" },
        { label: "Acknowledgement aim", value: "Within 2 business days" },
        { label: "External rights", value: "Remain available" },
      ]}
      sections={complaintSections}
      helpLabel="Raise a complaint?"
      helpText="Contact customer care"
    />
  );
}

const legalNoticeSections = [
  {
    id: "operator",
    title: "Store operator & contact",
    body: [
      `${siteConfig.brandName} is the trading brand presented on this online store. Customer service and formal store correspondence can be sent to ${supportEmail}.`,
      `Postal correspondence may be sent to ${siteConfig.contact.address}. Include an email address and order reference where relevant so we can acknowledge the notice.`,
    ],
  },
  {
    id: "purpose",
    title: "Purpose of the website",
    body: [
      "The website presents Reamflora products, supports online orders and provides customer-care and brand information. Public content is intended for visitors who can lawfully access and use it.",
      "A listing is an invitation to place an order and does not guarantee availability or acceptance. The terms and order policy explain when a purchase contract is formed.",
    ],
  },
  {
    id: "policies",
    title: "Policies that form the store terms",
    body: [
      "The terms and conditions are the main agreement for website use and purchases. Policies linked at checkout or from the policy centre add detail for privacy, payment, shipping, returns and related topics.",
      "If two store documents appear inconsistent, the more specific document applies to its topic, subject always to mandatory law and the express terms accepted for the order.",
    ],
    route: "/policies",
    routeLabel: "Open the policy centre",
  },
  {
    id: "rights",
    title: "Protected material & third-party rights",
    body: [
      "Store content is original to Reamflora or used with the appropriate permission. Third-party names, payment marks and service logos remain the property of their respective owners and are referenced only to identify compatible services where applicable.",
      "No part of this notice grants a licence beyond the limited personal use described in the intellectual-property policy.",
    ],
    route: "/intellectual-property",
    routeLabel: "Read intellectual property",
  },
  {
    id: "notices",
    title: "Sending a formal notice",
    body: [
      "A formal notice should identify the sender, the relevant order or page, the nature of the issue, the requested action and a reliable reply address. Electronic notices are treated as received when they reach our designated inbox in readable form.",
      "Do not include sensitive identity or financial information unless we request it through an appropriate channel. Ordinary customer questions do not need formal legal language.",
    ],
    contact: true,
    contactSuffix: " for electronic notices.",
  },
  {
    id: "law",
    title: "Applicable rules",
    body: [
      "The terms identify the contractual rules that apply to store use. Regardless of a contractual provision, consumers keep any protection and forum rights that their local law makes mandatory.",
      "Nothing on the site represents that every product or service is lawful or available in every location. You are responsible for lawful access and import into the chosen destination.",
    ],
  },
];

export function LegalNoticePolicy() {
  return (
    <PolicyDocument
      eyebrow="Legal"
      title="Legal notice"
      intro="Operator contact details, the website's purpose and how the Reamflora policy documents fit together."
      highlights={[
        { label: "Brand", value: siteConfig.brandName },
        { label: "Email", value: supportEmail },
        { label: "Postal contact", value: siteConfig.contact.address },
      ]}
      sections={legalNoticeSections}
      helpLabel="Formal notice?"
    />
  );
}

export const policyRouteComponents = {
  "/policies": PolicyCenter,
  "/orders": OrdersPolicy,
  "/payments": PaymentsPolicy,
  "/refunds": RefundPolicy,
  "/warranty": WarrantyPolicy,
  "/promotions": PromotionsPolicy,
  "/gift-cards": GiftCardsPolicy,
  "/product-care": ProductCarePolicy,
  "/customs": CustomsPolicy,
  "/accessibility": AccessibilityPolicy,
  "/intellectual-property": IntellectualPropertyPolicy,
  "/acceptable-use": AcceptableUsePolicy,
  "/disclaimer": DisclaimerPolicy,
  "/privacy-choices": PrivacyChoicesPolicy,
  "/complaints": ComplaintsPolicy,
  "/legal-notice": LegalNoticePolicy,
};

export const policySummary = {
  count: policyDirectory.reduce((total, group) => total + group.links.length, 0),
  defaultShipping: formatPrice(siteConfig.defaultShipping),
};
